import { useEffect, useState } from 'react';
import type { RegisterWrite } from '../../worker/protocol';
import { hex, SID_REG_NAMES } from '../util';

interface Props {
  fileId: string;
  version: unknown;
  tickRate: number;
  position: number;
  request: (
    fileId: string,
    fromTick: number,
    count: number,
  ) => Promise<{ rows: RegisterWrite[]; totalTicks: number }>;
}

const PAGE = 25;

/** Debug view: the raw SID register writes captured per frame. */
export function RegisterLog({ fileId, version, tickRate, position, request }: Props) {
  const [from, setFrom] = useState(0);
  const [rows, setRows] = useState<RegisterWrite[]>([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let live = true;
    void request(fileId, from, PAGE).then((r) => {
      if (!live) return;
      setRows(r.rows);
      setTotal(r.totalTicks);
    });
    return () => {
      live = false;
    };
  }, [fileId, from, version, request]);

  const byTick = new Map<number, RegisterWrite[]>();
  for (const r of rows) {
    const list = byTick.get(r.tick) ?? [];
    list.push(r);
    byTick.set(r.tick, list);
  }

  return (
    <div className="flex flex-col gap-2 text-[12px]">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn"
          onClick={() => setFrom(Math.max(0, from - PAGE))}
          disabled={from === 0}
        >
          ← Previous
        </button>
        <label className="flex items-center gap-1">
          <span className="label">From frame</span>
          <input
            type="number"
            className="field w-24"
            min={0}
            max={Math.max(0, total - 1)}
            value={from}
            onChange={(e) =>
              setFrom(Math.max(0, Math.min(Math.max(0, total - 1), Number(e.target.value) || 0)))
            }
          />
        </label>
        <button
          type="button"
          className="btn"
          onClick={() => setFrom(Math.min(Math.max(0, total - PAGE), from + PAGE))}
          disabled={from + PAGE >= total}
        >
          Next →
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => setFrom(Math.max(0, Math.floor(position * tickRate)))}
        >
          Jump to playhead
        </button>
        <span className="font-mono text-[11px] text-muted">
          {total} frames · {(1000 / tickRate).toFixed(2)} ms per frame
        </span>
      </div>
      <div className="max-h-[420px] overflow-auto rounded-md border border-line bg-canvas">
        <table className="w-full border-collapse font-mono text-xs">
          <thead className="sticky top-0 bg-panel2 text-left text-accent">
            <tr>
              <th scope="col" className="px-2 py-1">
                Frame
              </th>
              <th scope="col" className="px-2 py-1">
                Cycle
              </th>
              <th scope="col" className="px-2 py-1">
                Register
              </th>
              <th scope="col" className="px-2 py-1">
                Value
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: Math.min(PAGE, Math.max(0, total - from)) }, (_, i) => from + i).map(
              (tick) => {
                const list = byTick.get(tick) ?? [];
                if (!list.length) {
                  return (
                    <tr key={tick} className="border-t border-line text-muted">
                      <td className="px-2 py-0.5">{tick}</td>
                      <td className="px-2 py-0.5" colSpan={3}>
                        (no writes)
                      </td>
                    </tr>
                  );
                }
                return list.map((w, j) => (
                  <tr key={`${tick}-${j}`} className={j === 0 ? 'border-t border-line' : ''}>
                    <td className="px-2 py-0.5">{j === 0 ? tick : ''}</td>
                    <td className="px-2 py-0.5 text-muted">{w.cycle}</td>
                    <td className="px-2 py-0.5">
                      {hex(0xd400 + w.reg, 4)} <span className="text-muted">{SID_REG_NAMES[w.reg]}</span>
                    </td>
                    <td className="px-2 py-0.5">
                      {hex(w.value)}{' '}
                      <span className="text-muted">{w.value.toString(2).padStart(8, '0')}</span>
                    </td>
                  </tr>
                ));
              },
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
